'use client';

import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Carousel, CarouselItem } from '@/components/ui/carousel';
import { Accordion, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { formatTime, getMediaType, recordStat } from '@/shared/functions';
import { ArrowCircleRightIcon, ArrowCircleLeftIcon, PencilIcon } from "@phosphor-icons/react";
import { HotKeys, hotkeysList } from './hot-keys';
import MediaPane from './panes/mediaPane';
import DescriptionPane from './panes/descriptionPane';
import SourcesPane from './panes/sourcesPane';
import TitlePane from './panes/titlePane';
import TimePane from './panes/timePane';
import DatePane from './panes/datePane';
import HeaderPane from './panes/headerPane';
import LocationPane from './panes/locationPane';
import Select, { SingleValue, StylesConfig } from "react-select";
import { useSwipeable } from 'react-swipeable';
import { MediaObject, Slide, MediaType } from '@/shared/types';
import deepEqual from 'deep-equal';
import { MediaDisplay, type MediaDisplayRef } from './ui/mediaDisplay';
import { StatsContext, FullScreenContext } from '../app/contexts';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { UsableMediaTypes } from '@/shared/constants';
import { Button, Modal, ModalHeader, ModalBody, ModalContent } from '@heroui/react';
dayjs.extend(utc);
dayjs.extend(timezone);
export const maxDuration = 300; // allow the long requests for huge files

interface TPPresentationProps {
  title?: string;
  description?: string;
  media?: string;
  allSlides: Slide[];
  onOpenModal: (slide: Slide, column: string | null) => void;
  onOpenTitleSlideModal: (slide: Slide) => void;
  isEventModalOpen?: boolean;
  docIdMode?: boolean;
  featureFlags?: {
    [key: string]: any;
  };
}
interface DateItem {
  date: string;
  displayDate: string;
  events: Slide[];
}
const ACCORDION_TRANSITION = {
  x: {
    type: "spring",
    stiffness: 200,
    // Reduce for slower
    damping: 23,
    // Reduce to exaggerate 
    duration: 0.7
  },
  opacity: {
    type: "tween",
    duration: .7,
    ease: "easeInOut"
  },
  backgroundColor: {
    type: "tween",
    duration: 0.7
  },
  borderColor: {
    type: "tween",
    duration: 0.7
  }
};
const arrowVariants = {
  visible: {
    topRotate: 0,
    bottomRotate: 0,
    topD: "M9 12L15 6",
    bottomD: "M9 12L15 18"
  },
  hidden: {
    topRotate: 90,
    bottomRotate: -90,
    topD: "M9 12L15 6",
    bottomD: "M9 12L15 18"
  }
};
const AccordionContent = ({
  children,
  isOpen
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) => {
  return <AnimatePresence initial={false} data-sentry-element="AnimatePresence" data-sentry-component="AccordionContent" data-sentry-source-file="tp-presentation.tsx">
      <motion.div initial="collapsed" animate={isOpen ? "open" : "collapsed"} exit="collapsed" variants={{
      open: {
        opacity: 1,
        height: "auto"
      },
      collapsed: {
        opacity: 1,
        height: "7px"
      }
    }} transition={ACCORDION_TRANSITION} style={{
      overflow: 'hidden'
    }} data-sentry-element="unknown" data-sentry-source-file="tp-presentation.tsx">
        {children}
      </motion.div>
    </AnimatePresence>;
};
const TPPresentation: React.FC<TPPresentationProps> = ({
  title,
  description,
  media,
  allSlides,
  onOpenModal,
  onOpenTitleSlideModal,
  isEventModalOpen,
  docIdMode,
  featureFlags
}) => {
  const [selectedSlide, setSelectedSlide] = useState<Slide>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevButtonActive, setPrevButtonActive] = useState(false);
  const [nextButtonActive, setNextButtonActive] = useState(false);
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  const [isAccordionVisible, setIsAccordionVisible] = useState(true);
  const [isHotkeyAtBottom, setIsHotkeyAtBottom] = useState(false);
  const [loadedItems, setLoadedItems] = useState<number>(0);
  const [dateData, setDateData] = useState<any | null>(null);
  const [selectedEventIndex, setSelectedEventIndex] = useState<number>(0);
  const [sourcesExpanded, setSourcesExpanded] = useState<boolean>(false);
  const [allSlidesWithTitleSlide, setAllSlidesWithTitleSlide] = useState<Slide[]>();
  const [selectedDate, setSelectedDate] = useState<SingleValue<{
    value: number;
    label: string;
  }> | undefined>();
  const [selectedTime, setSelectedTime] = useState<SingleValue<{
    value: number;
    label: string;
  }> | undefined>();
  const [timeOptions, setTimeOptions] = useState<{
    value: number;
    label: string;
  }[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const accordionRef = useRef<HTMLDivElement>(null);
  const exportTimelineRef = useRef<HTMLDivElement>(null);
  const allSlidesOriginal = useRef<Slide[]>([]);
  const titleSlideOriginal = useRef<{
    title: string | undefined;
    description: string | undefined;
    media: string | undefined;
  }>({
    title: undefined,
    description: undefined,
    media: undefined
  });
  const allSlidesWithTitleSlideOriginal = useRef<Slide[]>([]);
  const currentSlideRef = useRef<Slide | undefined>(undefined);
  const mediaDisplayRef = useRef<MediaDisplayRef>(null);
  const statsContext = useContext(StatsContext);
  const {
    isFullscreen,
    setIsFullscreen,
    setFullscreenMediaItem
  } = useContext(FullScreenContext);
  const smoothScrollY = useMotionValue(0);
  const swipeX = useMotionValue(0);

  // Prefer MediaDisplay's toggleFullScreen so exit path preserves PDF page (currentPdfPageRef);
  // hotkey-only path would otherwise only call setIsFullscreen and lose the page.
  const handleMediaItemFullscreen = (goFullscreen: boolean) => {
    if (mediaDisplayRef.current) {
      if (goFullscreen !== isFullscreen) mediaDisplayRef.current.toggleFullScreen();
    } else {
      setIsFullscreen(goFullscreen);
    }
  };
  const handleExitFullscreen = () => {
    if (isFullscreen) {
      if (mediaDisplayRef.current) mediaDisplayRef.current.toggleFullScreen();else setIsFullscreen(false);
    }
  };
  const handleOpenModal = (slide: Slide) => {
    currentSlideRef.current = slide;
    if (slide.id === 'TITLE_SLIDE') {
      onOpenTitleSlideModal(slide);
    } else {
      onOpenModal(slide, null);
    }
  };
  useEffect(() => {
    if (allSlidesWithTitleSlide) {
      if (currentSlideRef.current && currentSlideRef.current.show) {
        const index = allSlidesWithTitleSlide.findIndex((s: Slide) => s.id === currentSlideRef.current?.id);
        setCurrentIndex(index);
        setSelectedSlide(allSlidesWithTitleSlide[index]);
      } else {
        currentSlideRef.current = allSlidesWithTitleSlide[0];
        setCurrentIndex(0);
        setSelectedSlide(currentSlideRef.current);
      }
    }
  }, [allSlidesWithTitleSlide]);
  useEffect(() => {
    const setupAllSlides = () => {
      allSlidesOriginal.current = allSlides;
      let mediaObject: MediaObject | null = null;
      if (media) {
        if (media.startsWith('s3://')) {
          mediaObject = media ? {
            mediaSrc: media,
            mediaUrl: media,
            mediaType: 'unknown' as MediaType
          } : null;
          if (mediaObject) {
            const mediaType: MediaType = getMediaType(mediaObject, mediaObject.sourceInfo);
            mediaObject.mediaType = mediaType;
          }
        }
      }
      const titleSlide: Slide | null = title || description || media ? {
        show: true,
        id: 'TITLE_SLIDE',
        slideTitle: title || 'Case Introduction',
        media: mediaObject ? [mediaObject] : [],
        sources: [],
        slideDescription: description,
        mediaType: media ? getMediaType({
          mediaSrc: media,
          mediaUrl: media,
          mediaType: 'unknown' as MediaType
        }, null) : 'Unknown' as MediaType
      } : null;
      titleSlideOriginal.current = {
        title,
        description,
        media
      };
      const newAllSlides = titleSlide ? [titleSlide, ...allSlides] : allSlides;
      allSlidesWithTitleSlideOriginal.current = newAllSlides;
      setAllSlidesWithTitleSlide(newAllSlides);
    };
    if (allSlides.length > 0 && (!deepEqual(allSlides, allSlidesOriginal.current) || !deepEqual({
      title,
      description,
      media
    }, titleSlideOriginal.current))) {
      setupAllSlides();
    }
  }, [allSlides, title, description, media]);
  const swipeHandlers = useSwipeable({
    swipeDuration: 300,
    onSwipedLeft: () => handleNavigation('next'),
    onSwipedRight: () => handleNavigation('previous'),
    onSwipedUp: () => {
      // Handle vertical swipe up if needed
    },
    onSwipedDown: () => {
      // Handle vertical swipe down if needed
    },
    trackTouch: true,
    trackMouse: true // Optional: allows swipe detection with mouse events
  });
  useEffect(() => {
    if (selectedDate && selectedDate.value !== undefined && dateData && allSlidesWithTitleSlide && allSlidesWithTitleSlide.length > 0) {
      const selectedDateIndex = selectedDate.value;
      const selectedDateItem = dateData.timeline[selectedDateIndex];
      const timeOptions = selectedDateItem?.events.map((event: Slide, index: number) => ({
        value: index,
        label: event.time ? `${event.time}` : "No Time Specified"
      }));
      setTimeOptions(timeOptions);
      if (selectedDateItem) {
        const slideTime = selectedDateItem.events[0].time;
        setSelectedSlide(selectedDateItem.events[0]);
        setSelectedEventIndex(0);
      }
    }
  }, [selectedDate, dateData, allSlidesWithTitleSlide]);
  const handleNavDateChange = (newValue: SingleValue<{
    value: number;
    label: string;
  }>) => {
    if (newValue) {
      setSelectedDate({
        value: newValue.value,
        label: newValue.label
      });
    }
  };
  useEffect(() => {
    if (selectedTime && selectedTime.value !== undefined && selectedDate && dateData && allSlidesWithTitleSlide && allSlidesWithTitleSlide.length > 0) {
      const selectedEventIndex = selectedTime.value;
      const selectedDateItem = dateData.timeline[selectedDate.value];
      const selectedTimeItem = selectedDateItem?.events[selectedEventIndex];
      if (selectedTimeItem) {
        handleSlideSelection(selectedTimeItem);
      }
    }
  }, [selectedTime, selectedDate, dateData, allSlidesWithTitleSlide]);
  const handleNavTimeChange = (newValue: SingleValue<{
    value: number;
    label: string;
  }>) => {
    if (newValue) {
      setSelectedTime({
        value: newValue.value,
        label: newValue.label
      });
    }
  };
  useEffect(() => {
    if (!!allSlidesWithTitleSlide) {
      // NOTE -- Jake Mason | (2026-02-11):
      // Pre-group by key so interleaved slides (Header #1, Header #2, Header #1) merge correctly with the same date.
      // This is required to fix TP-1644: https://tranquility-ai.atlassian.net/browse/TP-1644
      // We will end up with a timeline that sorts by the header slides first, then the events by date.
      const groupsByKey = new Map<string, {
        events: Slide[];
        firstOrder: number;
        displayDate: string;
      }>();
      let groupOrder = 0;
      allSlidesWithTitleSlide.forEach((slide: Slide) => {
        const dateOnly = slide.id === 'TITLE_SLIDE' ? 'TITLE_SLIDE' : slide.date?.split('T')[0];
        const date = dateOnly || undefined;
        const subTitle = slide.slideSubTitle && slide.slideSubTitle !== '' ? slide.slideSubTitle : undefined;
        const displayOrder = slide.displayOrder;
        let currentKey = '';
        let currentKeyType = '';
        if (subTitle && subTitle !== '') {
          currentKeyType = 'subTitle';
          currentKey = subTitle;
        } else if (date) {
          currentKeyType = 'date';
          currentKey = date;
        } else if (displayOrder) {
          currentKeyType = 'displayOrder';
          currentKey = displayOrder.toString();
        }
        const dateForDisplay = currentKeyType === 'date' ? currentKey : undefined;
        let displayDate = dateForDisplay === 'TITLE_SLIDE' ? title || 'Case Introduction' : dateForDisplay ? dayjs(dateForDisplay).utc().format('MMMM D, YYYY') : 'Date not specified';
        if (slide.slideSubTitle && slide.slideSubTitle !== '') {
          displayDate = currentKeyType === 'subTitle' ? currentKey : currentKeyType === 'displayOrder' ? `Slide # ${currentKey}` : 'Date not specified';
        }
        if (groupsByKey.has(currentKey)) {
          groupsByKey.get(currentKey)!.events.push(slide);
        } else {
          groupsByKey.set(currentKey, {
            events: [slide],
            firstOrder: groupOrder++,
            displayDate
          });
        }
      });

      // Sort by first occurrence order and build timeline
      const timeline = Array.from(groupsByKey.entries()).sort(([, a], [, b]) => a.firstOrder - b.firstOrder).map(([key, {
        events,
        displayDate
      }]) => ({
        date: key,
        displayDate,
        events
      }));
      const slideGroupsByDate = {
        timeline
      };
      if (slideGroupsByDate.timeline.length > 0) {
        setDateData(slideGroupsByDate);
        if (currentSlideRef.current && currentSlideRef.current.show) {
          const index = allSlidesWithTitleSlide.findIndex((s: Slide) => s.id === currentSlideRef.current?.id);
          setCurrentIndex(index);
          setSelectedSlide(allSlidesWithTitleSlide[index]);
        } else {
          currentSlideRef.current = slideGroupsByDate.timeline[0].events[0];
          setSelectedSlide(currentSlideRef.current);
          setCurrentIndex(0);
        }
      }
    }
  }, [allSlidesWithTitleSlide, title]);
  const updateAccordionState = useCallback((slide: Slide) => {
    if (dateData) {
      const selectedDate = dateData.timeline.find((date: {
        date: string;
        events: Slide[];
      }) => date.events.some(event => event.id === slide.id));
      if (selectedDate) {
        setOpenAccordionItems(prev => prev.includes(selectedDate.date) ? prev : [...prev, selectedDate.date]);
      }
    }
  }, [dateData]);
  const updateStyles = useCallback((slide: Slide) => {
    if (!dateData) return;
    const selectedDate = dateData.timeline.find((date: {
      events: {
        id: string;
      }[];
    }) => date.events.some((event: {
      id: string;
    }) => event.id === slide.id));
    if (!selectedDate) return;
    document.querySelectorAll('.accordion-trigger, ul[data-date], li[data-slide-id]').forEach(element => {
      const isSelected = element.getAttribute('data-date') === selectedDate.date || element.getAttribute('data-slide-id') === slide.id;
      if (element.classList.contains('accordion-trigger')) {
        // Toggle each class individually
        element.classList.toggle('border-[var(--tl-nav-selected)]', isSelected);
        element.classList.toggle('trigger-selected', isSelected);
        element.classList.toggle('border-[var(--tl-nav-unselected)]', !isSelected);
        element.classList.toggle('trigger-unselected', !isSelected);
      } else if (element.tagName.toLowerCase() === 'ul') {
        (element as HTMLElement).style.borderColor = isSelected ? 'var(--tl-nav-selected)' : 'var(--tl-nav-unselected)';
      } else if (element.tagName.toLowerCase() === 'li') {
        const gradientClass = isSelected ? 'bg-gradient-to-r from-[var(--tl-nav-selected)] via-[#0F1D32] to-[#0F1D32]' : 'bg-gradient-to-r from-[var(--tl-nav-unselected)] to-[#0F1D32]';
        element.classList.remove('bg-gradient-to-r', 'from-[var(--tl-nav-selected)]', 'from-[var(--tl-nav-unselected)]', 'via-[#0F1D32]', 'to-[#0F1D32]');
        gradientClass.split(' ').forEach(cls => element.classList.add(cls));
        const timeElement = element.querySelector('h4');
        if (timeElement) {
          timeElement.classList.toggle('text-white', isSelected);
          timeElement.classList.toggle('text-white/70', !isSelected);
        }
        const svgElements = element.querySelectorAll('svg circle, svg line');
        const strokeColor = isSelected ? 'var(--tl-nav-selected)' : 'var(--tl-nav-unselected)';
        svgElements.forEach(el => {
          el.setAttribute('stroke', strokeColor);
          if (el.tagName.toLowerCase() === 'circle') {
            el.setAttribute('fill', isSelected ? 'var(--tl-nav-selected)' : '#18293d');
          }
        });
      }
    });
  }, [dateData]);
  const handleNavigation = useCallback((direction: 'previous' | 'next') => {
    if (!allSlidesWithTitleSlide) return;
    let newIndex = currentIndex;
    if (direction === 'next' && currentIndex < allSlidesWithTitleSlide.length - 1) {
      newIndex = currentIndex + 1;
      setNextButtonActive(true);
      setTimeout(() => setNextButtonActive(false), 200);
    } else if (direction === 'previous' && currentIndex > 0) {
      newIndex = currentIndex - 1;
      setPrevButtonActive(true);
      setTimeout(() => setPrevButtonActive(false), 200);
    }
    if (newIndex !== currentIndex) {
      setFullscreenMediaItem(null);
      currentSlideRef.current = allSlidesWithTitleSlide[newIndex];
      setCurrentIndex(newIndex);
      setSelectedSlide(currentSlideRef.current);
      updateStyles(currentSlideRef.current);
    }
  }, [allSlidesWithTitleSlide, currentIndex, updateStyles]);
  const toggleAccordionVisibility = useCallback(() => {
    setIsAccordionVisible(prev => !prev);
  }, []);
  const toggleAllAccordionItems = useCallback(() => {
    if (dateData) {
      setOpenAccordionItems(prev => prev.length > 0 // If any accordions are expanded
      ? [] // Collapse all
      : dateData.timeline.map((item: {
        date: string;
      }) => item.date) // Otherwise expand all
      );
    }
  }, [dateData]);
  const scrollToTop = useCallback(() => {
    if (accordionRef.current) {
      accordionRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);
  const toggleScrollHotkey = useCallback(() => {
    if (accordionRef.current) {
      const {
        scrollHeight,
        clientHeight
      } = accordionRef.current;
      if (isHotkeyAtBottom) {
        accordionRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        accordionRef.current.scrollTo({
          top: scrollHeight - clientHeight,
          behavior: 'smooth'
        });
      }
      setIsHotkeyAtBottom(!isHotkeyAtBottom);
    }
  }, [isHotkeyAtBottom]);
  useEffect(() => {
    if (dateData && dateData.timeline && dateData.timeline.length > 0) {
      const firstEvent = currentSlideRef.current && currentSlideRef.current.show ? currentSlideRef.current : dateData.timeline[0].events[0];
      if (firstEvent) {
        currentSlideRef.current = firstEvent;
        const index = allSlidesWithTitleSlide?.findIndex((s: Slide) => s.id === currentSlideRef.current?.id) || 0;
        const slideToUpdate = allSlidesWithTitleSlide?.[index] || dateData.timeline[0].events[0];
        setCurrentIndex(index);
        setSelectedSlide(slideToUpdate);
        updateAccordionState(slideToUpdate);
        setTimeout(() => {
          updateStyles(slideToUpdate);
        }, 200);
      }
    }
  }, [dateData, updateAccordionState, updateStyles]);
  useEffect(() => {
    if (!allSlidesWithTitleSlide) return;
    if (!dateData || !selectedSlide) return;
    const index = allSlidesWithTitleSlide.findIndex((slide: Slide) => slide.id === selectedSlide?.id);
    setCurrentIndex(index);
    const selectedDate = dateData.timeline.find((date: {
      date: string;
      events: Slide[];
    }) => date.events.some(event => event.id === selectedSlide.id));
    if (selectedDate) {
      setOpenAccordionItems(prev => prev.includes(selectedDate.date) ? prev : [...prev, selectedDate.date]);
      setTimeout(() => {
        const selectedLi = document.querySelector(`li[data-slide-id="${selectedSlide.id}"]`);
        if (selectedLi && accordionRef.current) {
          const accordionRect = accordionRef.current.getBoundingClientRect();
          const selectedLiRect = selectedLi.getBoundingClientRect();
          const viewportHeight = accordionRef.current.clientHeight;
          const scrollTop = selectedLiRect.top + accordionRef.current.scrollTop - accordionRect.top;
          const targetScrollTop = scrollTop - viewportHeight / 2 + selectedLiRect.height / 2;
          accordionRef.current.scrollTo({
            top: targetScrollTop,
            left: accordionRef.current.scrollLeft,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [selectedSlide, allSlidesWithTitleSlide, dateData]);

  // Event handlers
  const handleSlideSelection = useCallback((slide: Slide) => {
    setSelectedSlide(slide);
    const selectedDate = dateData.timeline.find((date: {
      date: string;
      events: Slide[];
    }) => date.events.some((event: Slide) => event.id === slide.id));
    if (selectedDate) {
      setOpenAccordionItems(prev => prev.includes(selectedDate.date) ? prev : [...prev, selectedDate.date]);
    }
    updateStyles(slide);
  }, [dateData, updateStyles]);
  const getTruncatedTitle = useCallback((slide: Slide) => {
    const title = slide.slideTitle || 'Untitled';
    return title.length > 30 ? `${title.substring(0, 30)}...` : title;
  }, []);
  const getPreviousSlide = useCallback(() => {
    if (!allSlidesWithTitleSlide) return;
    return currentIndex > 0 ? allSlidesWithTitleSlide[currentIndex - 1] : null;
  }, [allSlidesWithTitleSlide, currentIndex]);
  const getNextSlide = useCallback(() => {
    if (!allSlidesWithTitleSlide) return;
    return currentIndex < allSlidesWithTitleSlide.length - 1 ? allSlidesWithTitleSlide[currentIndex + 1] : null;
  }, [allSlidesWithTitleSlide, currentIndex]);

  // Add this effect to load items sequentially
  useEffect(() => {
    if (dateData?.timeline) {
      const timer = setInterval(() => {
        setLoadedItems(prev => {
          if (prev < dateData.timeline.length) {
            return prev + 1;
          }
          clearInterval(timer);
          return prev;
        });
      }, 75); // Adjust timing as needed (100ms between each item)

      return () => clearInterval(timer);
    }
  }, [dateData]);
  const toggleCurrentDayAccordion = useCallback(() => {
    if (dateData && selectedSlide) {
      const selectedDate = dateData.timeline.find((date: {
        date: string;
        events: Slide[];
      }) => date.events.some(event => event.id === selectedSlide.id));
      if (selectedDate) {
        setOpenAccordionItems(prev => prev.includes(selectedDate.date) ? prev.filter(item => item !== selectedDate.date) : [...prev, selectedDate.date]);
      }
    }
  }, [dateData, selectedSlide]);
  const navigateToDay = useCallback((direction: 'previous' | 'next') => {
    if (!dateData || !selectedSlide) return;
    const currentDayIndex = dateData.timeline.findIndex((date: {
      date: string;
      events: Slide[];
    }) => date.events.some(event => event.id === selectedSlide.id));
    if (currentDayIndex === -1) return;
    const newDayIndex = direction === 'previous' ? Math.max(0, currentDayIndex - 1) : Math.min(dateData.timeline.length - 1, currentDayIndex + 1);
    if (newDayIndex === currentDayIndex) return;
    const currentDay = dateData.timeline[currentDayIndex];
    const newDay = dateData.timeline[newDayIndex];
    const newSlide = newDay.events[0];
    setOpenAccordionItems(prev => {
      const withoutCurrent = prev.filter(item => item !== currentDay.date);
      return [...withoutCurrent, newDay.date];
    });
    currentSlideRef.current = newSlide;
    setSelectedSlide(currentSlideRef.current);
    updateStyles(currentSlideRef.current as Slide);
    setTimeout(() => {
      const trigger = document.querySelector(`[data-date="${newDay.date}"]`);
      if (trigger && accordionRef.current) {
        trigger.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }, 100);
  }, [dateData, selectedSlide, updateStyles]);
  const jumpToDay = useCallback((position: 'first' | 'last') => {
    if (!dateData || dateData.timeline.length === 0) return;

    // Get the target day and its first event
    const targetDay = position === 'first' ? dateData.timeline[0] : dateData.timeline[dateData.timeline.length - 1];
    const newSlide = targetDay.events[0];
    setOpenAccordionItems(prev => {
      if (selectedSlide) {
        const currentDay = dateData.timeline.find((date: {
          events: {
            id: string;
          }[];
        }) => date.events.some((event: {
          id: string;
        }) => event.id === selectedSlide.id));
        prev = prev.filter(item => item !== currentDay?.date);
      }
      return [...prev, targetDay.date];
    });
    currentSlideRef.current = newSlide;
    setSelectedSlide(currentSlideRef.current);
    updateStyles(currentSlideRef.current as Slide);
    setTimeout(() => {
      const trigger = document.querySelector(`[data-date="${targetDay.date}"]`);
      if (trigger && accordionRef.current) {
        trigger.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }, 100);
  }, [dateData, selectedSlide, updateStyles]);
  const getHotkeyForAction = (action: string): string => {
    for (const section of hotkeysList) {
      const shortcut = section.shortcuts.find(s => s.description.toLowerCase().includes(action.toLowerCase()));
      if (shortcut) {
        return shortcut.keys.join('+');
      }
    }
    return '';
  };
  const handleExport = async () => {
    const exportTimelineElement = exportTimelineRef.current;
    exportTimelineElement?.classList.remove('h-[calc(100vh-150px)]');
    if (exportTimelineElement) {
      const opt: any = {
        margin: [10, 10, 10, 10],
        pagebreak: {
          mode: ['avoid-all']
        },
        jsPDF: {
          orientation: 'landscape',
          format: 'letter'
        }
      };
      const html2pdf = (await import("html2pdf.js")).default;
      html2pdf().set(opt).from(exportTimelineElement).save('timeline.pdf');
      setIsExporting(false);
    }
  };
  const [isExporting, setIsExporting] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'p' || e.key === 'P')) {
        // setIsExporting(true);
      }
      if (e.ctrlKey && e.shiftKey && (e.key === 'e' || e.key === 'E')) {
        if (selectedSlide) {
          handleOpenModal(selectedSlide);
        }
      }
    };
    (window as any).addEventListener('keydown', handleKeyDown);
    return () => {
      (window as any).removeEventListener('keydown', handleKeyDown);
    };
  }, [onOpenModal, selectedSlide]);
  useEffect(() => {
    setSourcesExpanded(false);
  }, [selectedSlide?.id]);
  const GRADIENT_STYLES = {
    selected: 'bg-gradient-to-r from-[var(--tl-nav-selected)] via-[var(--tl-nav-selected)] to-transparent',
    unselected: 'bg-gradient-to-r from-[var(--tl-nav-unselected)] via-[var(--tl-nav-unselected)] to-transparent',
    hover: 'hover:bg-gradient-to-r hover:from-[var(--tl-nav-selected)] via-[var(--tl-nav-selected)] hover:to-transparent'
  } as const;
  if (!dateData || !dateData.timeline || !selectedSlide) {
    if (!!allSlidesWithTitleSlide || (allSlidesWithTitleSlide || []).length < 1) {
      return <div className="flex flex-col">
          <div className='flex flex-col sm:h-[calc(100vh-62px)] items-center justify-center overflow-hidden'>
            <h1 className="text-4xl text-white">No events are visible or created for this timeline</h1>
          </div>
        </div>;
    }
    return <div className="flex flex-col">
        <div className='flex flex-col sm:h-[calc(100vh-62px)] items-center justify-center overflow-hidden'>
          {docIdMode ? <h1 className="text-4xl text-white">Enter Document ID above to view its timeline</h1> : <>
              <h1 className="text-4xl text-white mb-8">Please select a timeline</h1>
            <Button onPress={() => setIsModalOpen(true)} className="cursor-pointer text-lg sm:text-2xl py-6 px-8 font-light animate-glow [box-shadow:0_0_8px_rgba(89,162,247,0.6),0_0_16px_rgba(89,162,247,0.4),0_0_24px_rgba(89,162,247,0.3)]">Timelines Tutorial</Button>
            <Modal isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
              <ModalContent className="max-w-4xl max-h-[90vh]">
                <ModalHeader className="flex flex-col gap-1">
                </ModalHeader>
                <ModalBody className="p-0">
                  <iframe src="https://tranquility.ai/get-started-guide#timeline" title="Timelines Tutorial" className="w-full h-[70vh] rounded-lg" />
                </ModalBody>
              </ModalContent>
            </Modal>
            </>}
        </div>
      </div>;
  }
  if (isExporting && allSlidesWithTitleSlide) {
    return <div className="flex flex-col">
        <div className='top-[0px] flex flex-col gap-2 inset-0 z-50 justify-left bg-white border-1 border-black'>
          <div className='flex flex-row gap-2 justify-left top-0 left-20 h-[30px] w-full p-[10px] bg-blue-900'>
            <button data-testid="CancelExportButton" className='flex flex-row items-center justify-center bg-gray-500 text-white rounded-md p-2' onClick={event => {
            setIsExporting(false);
            recordStat('CancelExportButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
          }}>
              CANCEL
            </button>
            <button data-testid="ExportButton" className='flex flex-row items-center justify-center bg-blue-700 text-white rounded-md p-2' onClick={event => {
            handleExport();
            recordStat('ExportButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
          }}>
              EXPORT
            </button>
          </div>
          <div ref={exportTimelineRef} className='h-[calc(100vh-150px)] left-[20px] flex flex-col gap-2 text-black overflow-y-scroll'>
            {allSlidesWithTitleSlide.map(slide => <div key={slide.id} className='flex flex-col gap-2 text-black h-fit'>
                <div className='flex flex-row gap-2'>
                  <div>{slide.date}</div>
                  <div>{slide.time}</div>
                </div>
                <div className='text-lg font-bold'>{slide.slideTitle}</div>
                <div className='flex flex-row gap-2 justify-left'>
                  <div className='text-md w-[300px] sm:w-[500px]'>
                    <ReactMarkdown>{slide.slideDescription}</ReactMarkdown>
                  </div>
                  <div className='w-[300px] sm:w-[500px]'>
                    {slide.media?.[0] && <MediaDisplay mediaItem={slide.media?.[0]} />}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>;
  }
  return <div className="flex flex-col" data-sentry-component="TPPresentation" data-sentry-source-file="tp-presentation.tsx">
      <div {...swipeHandlers} data-testid="timelinePresentationView" className={`relative max-w-[100vw] sm:scrollbar-hide sm:overflow-none overscroll-none sm:h-screen
          sm:bg-texture bg-no-repeat mt-[15px] mb-[200px] ${featureFlags?.workProductNav ? featureFlags?.enableMediaAndSourceShare ? 'sm:mb-[119px]' : 'sm:mb-[80px]' : 'sm:mb-[0px]'}
        `}>
      <motion.div style={{
        x: swipeX
      }} transition={{
        type: "spring",
        stiffness: 300,
        damping: 30
      }} data-sentry-element="unknown" data-sentry-source-file="tp-presentation.tsx">
        {!isEventModalOpen && <HotKeys onNavigate={handleNavigation} onDayNavigate={navigateToDay} onJumpToDay={jumpToDay} onToggleAccordion={toggleAccordionVisibility} onToggleAllAccordions={toggleAllAccordionItems} onToggleCurrentDay={toggleCurrentDayAccordion} onScrollToTop={scrollToTop} onToggleScroll={toggleScrollHotkey} onExitFullscreen={handleExitFullscreen} onToggleImageFullscreen={() => handleMediaItemFullscreen(!isFullscreen)} isFullscreen={isFullscreen} selectedMediaType={selectedSlide?.mediaType} selectedMediaSrc={selectedSlide?.mediaSrc} />}
        {!isExporting && allSlidesWithTitleSlide && <div className='flex'>
          {/* -------------------Begin Timeline Nav-------------------*/}
          {!isFullscreen && <div className='hidden sm:flex sm:flex-col'>
            <motion.div className='relative' animate={{
              width: isAccordionVisible ? '300px' : '0px',
              x: isAccordionVisible ? 0 : -300,
              opacity: isAccordionVisible ? 1 : 0,
              backgroundColor: isAccordionVisible ? 'rgba(24, 41, 61, 0.5)' : 'rgba(24, 41, 61, 0)',
              borderColor: isAccordionVisible ? 'var(--tl-nav-selected)' : 'var(--tl-nav-unselected)'
            }} transition={ACCORDION_TRANSITION}>
              <motion.div ref={accordionRef} className={`w-[265px] border-r-[5px] px-4 border-r-[#09121E] border-solid sm:h-[calc(100vh)] drop-shadow-md shadow-inner overflow-y-auto overflow-x-hidden scrollbar-hide overscroll-none`} id='accordion' onScroll={() => {
                if (accordionRef.current) {
                  smoothScrollY.set(-accordionRef.current.scrollTop);
                }
              }}>
                <div className='flex sticky top-0 left-0 pt-[10px] h-[50px] mx-[-16px] items-center justify-left bg-gradient-to-b from-[#09121E] via-[#09121E] to-transparent z-10'>
                  <div id="ToggleAllAccordionItemsButton" role="button" tabIndex={0} aria-label="Toggle all accordion items" className='flex flex-row items-center h-[35px] w-[250px] ml-[16px] pl-[11px] pr-[2px] bg-[var(--tl-nav-unselected)] border-2 border-solid border-[var(--tl-nav-unselected)] shadow gap-1 hover:cursor-pointer font-inter text-white rounded-full hover:bg-gradient-to-br hover:from-transparent hover:via-white/10 hover:to-transparent focus:outline-none' onClick={event => {
                    toggleAllAccordionItems();
                    recordStat('ToggleAllAccordionItemsButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                  }} onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleAllAccordionItems();
                      recordStat('ToggleAllAccordionItemsButtonClicked', event as any, statsContext, !!featureFlags?.useIntercom);
                    }
                  }}>
                    <span className='grow truncate text-left uppercase text-white text-sm font-bold tracking-wider'><span className='font-bold'>{allSlides.length} </span>Events in Timeline</span>
                    <div className='flex flex-row w-fit items-center gap-1'>
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 transform transition-transform duration-300 ${openAccordionItems.length === dateData.timeline.length ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
                <Accordion type="multiple" value={openAccordionItems} onValueChange={setOpenAccordionItems}>
                  {dateData.timeline.slice(0, loadedItems).map((dateItem: DateItem, index: number) => <motion.div key={index} initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.1,
                    ease: "easeInOut"
                  }}>
                      <AccordionItem value={dateItem.date} className='border-0 m-0'>
                        <AccordionTrigger data-testid="ToggleAccordionItemButton" tabIndex={0} aria-label={`Toggle ${dateItem.displayDate} events`} className="accordion-trigger h-[35px] w-full pl-2.5 pr-1.5 py-0 flex items-center justify-between bg-[#18293d]/[.2] rounded-[32px] text-white text-sm font-inter font-normal leading-[14px] tracking-wider shadow border-solid border-2 hover:no-underline hover:cursor-pointer hover:bg-gradient-to-br hover:from-white/30 hover:via-white/10 hover:to-transparent truncate border-[var(--tl-nav-unselected)] outline-none" data-date={dateItem.date} onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        setOpenAccordionItems(prev => prev.includes(dateItem.date) ? prev.filter(item => item !== dateItem.date) : [...prev, dateItem.date]);
                        recordStat('ToggleAccordionItemButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                      }}>
                          <span className='truncate flex-1'>{dateItem.displayDate}</span>
                          <span className={`inline-flex items-center justify-center text-white text-xs font-medium ${dateItem.events.length > 9 ? 'px-2 rounded-full' : 'w-5 h-5 rounded-full'} bg-white/20 mr-1`}>
                            {dateItem.events.length}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent isOpen={openAccordionItems.includes(dateItem.date)}>
                          <ul className='flex flex-col space-y-2 m-0 ml-4 pr-0 pb-4 pl-8 gap-2 border-l-[2px] border-solid relative border-[var(--tl-nav-unselected)]' data-date={dateItem.date}>
                            {dateItem.events.map((timeItem, timeIndex: number) => <li data-testid="TimelineItemButton" key={timeIndex} data-slide-id={timeItem.id} role="button" tabIndex={0} aria-label={`Select event: ${timeItem.slideTitle || timeItem.slideSubTitle || 'Untitled event'}`} className={`
                                    flex items-center w-fit h-fit m-0 mt-4 rounded flex-col justify-center gap-1.5 cursor-pointer
                                    transition-all duration-200 ease-in-out
                                    origin-left
                                    ${selectedSlide?.id === timeItem.id ? `scale-105 ${GRADIENT_STYLES.selected}` : `scale-100 ${GRADIENT_STYLES.unselected}`}
                                    ${GRADIENT_STYLES.hover}
                                  `} onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleSlideSelection(timeItem);
                            recordStat('TimelineItemButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                          }} onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              event.stopPropagation();
                              handleSlideSelection(timeItem);
                              recordStat('TimelineItemButtonClicked', event as any, statsContext, !!featureFlags?.useIntercom);
                            }
                          }}>
                                <div className="absolute flex items-center -left-[40px]">
                                  <svg width='40' height='14' viewBox='0 0 40 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                    <circle cx={timeItem.id === selectedSlide?.id ? '8.5' : '7'} cy='7' r='6' fill={timeItem.id === selectedSlide?.id ? 'var(--tl-nav-selected)' : '#18293d'} stroke={timeItem.id === selectedSlide?.id ? 'var(--tl-nav-selected)' : 'var(--tl-nav-unselected)'} strokeWidth='2' />
                                    <line x1='14' y1='7' x2='40' y2='7' stroke={timeItem.id === selectedSlide?.id ? 'var(--tl-nav-selected)' : 'var(--tl-nav-unselected)'} strokeWidth='2' />
                                  </svg>
                                </div>
                                <div className='flex flex-col gap-2 bg-[#0F1D32] m-[2px] p-[10px] justify-center items-center group min-h-[90px] min-w-[136px]'>
                                  {timeItem.time && <h4 className="m-0 p-0 text-sm font-normal leading-[14px] tracking-wide text-white/70">
                                      {dateItem.date !== 'Date not specified' && timeItem.isTimeValid && timeItem.time ? formatTime(timeItem.time) : 'Time Not Specified'}
                                    </h4>}
                                  <div className={`flex flex-col items-center justify-center max-h-[221px] w-[138px] h-fit ${timeItem.mediaSrc || timeItem.media?.[0] ? '' : 'bg-white/5 border-black/20 border-[1px] border-solid'} min-h-[90px] overflow-hidden rounded m-0 p-0`}>
                                    {!timeItem.mediaSrc && !timeItem.media?.[0] && <div className='p-2 flex items-center h-full'>
                                        {timeItem.id === 'TITLE_SLIDE' ? <span className='text-center line-clamp-3 overflow-hidden text-ellipsis'>Case Introduction</span> : <span className='text-center line-clamp-3 overflow-hidden text-ellipsis'>{timeItem.slideTitle || timeItem.slideSubTitle}</span>}
                                      </div>}
                                    {timeItem.mediaType && UsableMediaTypes.includes(timeItem.mediaType) && (timeItem.mediaSrc || timeItem.media?.[0]) && <MediaDisplay mediaItem={timeItem.media?.[0] ? timeItem.media?.[0] : {
                                  mediaType: timeItem.mediaType,
                                  mediaSrc: timeItem.mediaSrc || timeItem.media?.[0]?.mediaSrc,
                                  mediaContext: timeItem.media?.[0]?.mediaContext || undefined
                                }} isAccordion />}
                                    {timeItem.mediaType === 'youtube' && (timeItem.mediaSrc || timeItem.media?.[0]) && <img data-testid="YoutubeItemButton" src={`https://img.youtube.com/vi/${timeItem.mediaSrc?.split('/').pop() || timeItem.media?.[0]?.mediaSrc?.split('/').pop()}/0.jpg`} alt={timeItem.slideTitle || timeItem.slideSubTitle || ''} width={160} height={90} className='w-full h-full object-cover rounded cursor-pointer' />}
                                  </div>
                                  {(timeItem.mediaSrc || timeItem.media?.[0]) && <h4 className="m-0 p-0 text-sm font-normal leading-[14px] tracking-wide text-white/70 text-center w-[136px] truncate">
                                      {timeItem.slideTitle || timeItem.slideSubTitle || ''}
                                    </h4>}
                                </div>
                              </li>)}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    </motion.div>)}
                </Accordion>
                {loadedItems === dateData.timeline.length && <motion.button data-testid="ScrollToTopButton" tabIndex={0} aria-label="Scroll to top of timeline" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 0.5,
                  ease: "easeInOut"
                }} className='flex flex-row items-center h-[35px] w-[243px] pl-[11px] pr-[7px] bg-[var(--tl-nav-unselected)] border-2 border-solid border-[var(--tl-nav-unselected)] shadow gap-1 hover:cursor-pointer text-white font-inter rounded-full hover:bg-gradient-to-br hover:from-transparent hover:via-white/10 hover:to-transparent focus:outline-none mb-[250px]' onClick={event => {
                  scrollToTop();
                  recordStat('ScrollToTopButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                }}>
                    <span className='grow truncate text-left uppercase text-white text-sm font-bold tracking-wider'>End of Timeline</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                  </motion.button>}
              </motion.div>
              <div className='flex sticky bottom-0 left-0 pt-[10px] h-[150px] mx-[-16px] w-[300px] bg-gradient-to-t from-[#09121E] to-transparent z-10 pointer-events-none'></div>
            </motion.div>
            <motion.div data-testid="ToggleAccordionVisibilityButton" role="button" tabIndex={0} aria-label={isAccordionVisible ? "Hide timeline sidebar" : "Show timeline sidebar"} className='absolute top-[4px] w-[30px] h-[50px] bg-[rgba(24, 41, 61, 0.5)] rounded-r-lg cursor-pointer flex items-center justify-center drop-shadow-md shadow-inner group border-black/50 border-solid border-r-[3px] border-b-[4px] border-t-[1px] border-l-0' initial={{
              x: 300,
              opacity: 0
            }} animate={{
              x: isAccordionVisible ? 300 : 0,
              opacity: 1,
              backgroundColor: isAccordionVisible ? 'rgba(24, 41, 61, 0.5)' : 'rgba(24, 41, 61, 0.5)'
            }} transition={ACCORDION_TRANSITION} onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              toggleAccordionVisibility();
              recordStat('ToggleAccordionVisibilityButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
            }} onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                toggleAccordionVisibility();
                recordStat('ToggleAccordionVisibilityButtonClicked', event as any, statsContext, !!featureFlags?.useIntercom);
              }
            }}>
                <motion.div className="p-0" animate={isAccordionVisible ? "visible" : "hidden"} transition={{
                duration: 0.5
              }}>
                  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='var(--tl-nav-selected)' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' className='text-white/80 rounded-sm'>
                    <motion.path d={arrowVariants.visible.topD} animate={{
                    rotate: isAccordionVisible ? 0 : -90
                  }} transition={{
                    duration: 0.5,
                    ease: "easeInOut"
                  }} style={{
                    transformOrigin: '12px 12px'
                  }} />
                    <motion.path d={arrowVariants.visible.bottomD} animate={{
                    rotate: isAccordionVisible ? 0 : 90
                  }} transition={{
                    duration: 0.5,
                    ease: "easeInOut"
                  }} style={{
                    transformOrigin: '12px 12px'
                  }} />
                </svg>
              </motion.div>
            </motion.div>
          </div>}
          {/* -------------------Begin Carousel-------------------*/}
            <Carousel className={`carousel flex overflow-y-scroll sm:overflow-y-none flex-col items-start justify-start
                sm:justify-center h-screen sm:mx-[30px] w-full sm:overflow-hidden
                ${featureFlags?.workProductNav ? 'sm:h-[calc(100vh-147px)]' : 'sm:h-[calc(100vh-110px)]'}
                ${isFullscreen ? '!h-[1px] !w-[1px] sm:!h-[1px] sm:!w-[1px] overflow-hidden opacity-[0.01]' : ''}
              `}>
              <div className='relative z-[9999999] flex flex-row gap-2 items-center justify-center w-full sm:hidden pb-[10px] sm:pb-[0px]'>
                <Select styles={{
                control: state => ({
                  ...state,
                  borderRadius: '6px',
                  backgroundColor: '#091523/2',
                  border: 'none',
                  padding: '0 8px 0 10px',
                  fontSize: '16px',
                  color: 'white'
                }),
                singleValue: provided => ({
                  ...provided,
                  color: 'white'
                }),
                placeholder: provided => ({
                  ...provided,
                  color: "#FFFFFF"
                }),
                input: provided => ({
                  ...provided,
                  color: "#FFFFFF"
                }),
                dropdownIndicator: state => ({
                  ...state,
                  padding: '0 0 2px 2px',
                  color: '#ffffff/50'
                }),
                menu: state => ({
                  ...state,
                  padding: '0px',
                  height: '80px'
                }),
                menuList: state => ({
                  ...state,
                  padding: '0px',
                  height: '80px'
                }),
                valueContainer: state => ({
                  ...state,
                  justifyContent: 'left',
                  padding: '0 0 2px 0',
                  color: 'white'
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#ffffff' : 'transparent',
                  color: state.isFocused ? 'black' : 'black',
                  fontWeight: state.isFocused ? 'normal' : 'normal',
                  ':active': {
                    backgroundColor: '#ffffff'
                  }
                })
              } as StylesConfig} className={`font-inter w-full ml-2.5 slide-boxes`} classNamePrefix="react-select" aria-label="Select date" value={selectedSlide?.slideSubTitle && selectedSlide?.slideSubTitle.trim() !== '' ? selectedSlide?.slideSubTitle : selectedSlide?.displayDate} onChange={handleNavDateChange as (newValue: unknown) => void} options={dateData.timeline.map((dateItem: DateItem, index: number) => ({
                value: index,
                label: `${dateItem.displayDate}`
              }))} noOptionsMessage={() => "No events"} placeholder={selectedSlide?.slideSubTitle && selectedSlide?.slideSubTitle.trim() !== '' ? selectedSlide?.slideSubTitle : selectedSlide?.displayDate} />
                <Select styles={{
                control: state => ({
                  ...state,
                  borderRadius: '6px',
                  backgroundColor: '#091523/2',
                  border: 'none',
                  padding: '0 8px 0 10px',
                  fontSize: '16px',
                  ...(selectedSlide?.time ? {} : {
                    fontSize: '0.875rem'
                  })
                }),
                singleValue: provided => ({
                  ...provided,
                  color: 'white'
                }),
                placeholder: provided => ({
                  ...provided,
                  color: "#FFFFFF"
                }),
                input: provided => ({
                  ...provided,
                  color: "#FFFFFF"
                }),
                dropdownIndicator: state => ({
                  ...state,
                  padding: '0 0 2px 2px',
                  color: 'white'
                }),
                valueContainer: state => ({
                  ...state,
                  justifyContent: 'left',
                  padding: '0 0 2px 0',
                  color: 'white'
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#ffffff' : 'transparent',
                  color: state.isFocused ? 'black' : 'black',
                  fontWeight: state.isFocused ? 'normal' : 'normal',
                  ':active': {
                    backgroundColor: '#ffffff'
                  }
                })
              } as StylesConfig} className={`font-inter w-full mr-2.5 slide-boxes`} classNamePrefix="react-select" aria-label="Select time" value={selectedEventIndex} onChange={handleNavTimeChange as (newValue: unknown) => void} options={timeOptions} noOptionsMessage={() => "No events"} placeholder={selectedSlide?.time ? `${selectedSlide.time}` : "No Time Specified"} />
              </div>
              <CarouselItem className={`flex flex-col gap-[10px] sm:gap-[30px] w-full p-0 pb-[100px] sm:pb-[0px]
                ${featureFlags?.workProductNav ? 'sm:h-[calc(100vh-200px)] sm:max-h-[calc(100vh-200px)]' : 'sm:h-[calc(100vh-110px)] sm:max-h-[calc(100vh-110px)]'}
                overflow-visible sm:overflow-scroll sm:scrollbar-hide sm:overscroll-none custom-scrollbar2`}>
                <div className='flex flex-row items-start sm:items-center h-full p-0'>
                  <div data-testid='previous-button' className='hidden sm:block'>
                    <button data-testid="PreviousButton" onClick={event => {
                    handleNavigation('previous');
                    recordStat('PreviousSlideButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                  }} disabled={currentIndex === 0} className={`px-2 py-1.5 h-fit w-fit flex-none rounded-full bg-transparent outline-none ${currentIndex > 0 ? 'cursor-pointer hover:bg-slate-700/30' : ''} text-white`}>
                      <motion.div className='bg-transparent outline-none' initial={false} animate={{
                      color: prevButtonActive ? '#63b4ff' : 'white',
                      scale: currentIndex > 0 ? 1 : 1,
                      opacity: currentIndex > 0 ? 1 : 0.5
                    }} whileHover={{
                      scale: currentIndex < allSlidesWithTitleSlide.length - 1 ? 1.20 : 1
                    }} whileTap={{
                      scale: currentIndex < allSlidesWithTitleSlide.length - 1 ? 0.95 : 1
                    }} transition={{
                      duration: 0.3
                    }}>
                        <ArrowCircleLeftIcon weight="duotone" className={`h-12 w-12 pt-[2px] ${currentIndex === 0 ? 'opacity-20' : ''}`} />
                      </motion.div>
                    </button>
                  </div>
                  <div data-testid='event-container' className='box-border flex flex-col w-[calc(100%-20px)] sm:w-[calc(100%-188px)] h-fit sm:h-full mx-[10px] sm:mx-[30px] pb-[80px] sm:pb-[0px] p-0'>
                    <div data-testid='event-entities' className='hidden sm:flex flex-row items-baseline justify-start gap-[10px] mb-[10px] sm:mb-[30px]'>
                      {selectedSlide.id !== 'TITLE_SLIDE' && selectedSlide.slideSubTitle && <HeaderPane slide={selectedSlide} />}
                      {selectedSlide.id !== 'TITLE_SLIDE' && !selectedSlide.slideSubTitle && <DatePane slide={selectedSlide} />}
                      {selectedSlide.id !== 'TITLE_SLIDE' && !selectedSlide.slideSubTitle && selectedSlide.time && selectedSlide.isTimeValid && <TimePane slide={selectedSlide} />}
                      {(selectedSlide.location?.name || selectedSlide.location?.address) && <LocationPane slide={selectedSlide} />}
                    </div>
                    {selectedSlide.slideTitle && <TitlePane slide={selectedSlide} />}
                    <div data-testid='slide-content' className={`object-contain sm:max-h-[calc(100vh-250px)] mb-[20px] box-border flex flex-col justify-start
                        ${selectedSlide.slideDescription ? 'sm:flex-row sm:justify-evenly gap-[10px] sm:gap-[30px]' : 'sm:flex-col sm:justify-evenly sm:items-center gap-[10px] sm:gap-[20px]'} sm:h-full w-full`}>
                      {selectedSlide.media && selectedSlide.media.length > 0 && <>
                        <div className={`box-border ${selectedSlide.slideDescription ? 'w-full sm:w-1/2' : `w-full max-w-6xl mx-auto ${sourcesExpanded ? 'h-[80%]' : 'sm:h-[92%]'}`}`}>
                          <MediaPane slide={selectedSlide} isPresentation={true} sourcesExpanded={sourcesExpanded} hasDescription={!!selectedSlide.slideDescription} setFullscreenMediaItem={setFullscreenMediaItem} isFullscreen={isFullscreen} mediaDisplayRef={mediaDisplayRef} />
                        </div>
                        {!selectedSlide.slideDescription && selectedSlide.sources && selectedSlide.sources.length > 0 && <div className={`flex w-full max-w-6xl mx-auto justify-end ${sourcesExpanded ? 'mt-4' : ''}`}>
                            <SourcesPane slide={selectedSlide} onToggle={setSourcesExpanded} />
                          </div>}
                        </>}
                      {!isFullscreen && selectedSlide.slideDescription && <div className={`sm:w-1/2 w-full ${selectedSlide.media && selectedSlide.media.length > 0 ? 'w-1/2' : 'w-full sm:w-[calc(100%-5px)]'} flex flex-col gap-[10px] sm:gap-[30px]`}>
                          <div className='flex flex-row justify-start h-full overflow-y-auto custom-scrollbar2'>
                            <DescriptionPane slide={selectedSlide} />
                          </div>
                          {selectedSlide.sources && selectedSlide.sources.length > 0 && <div className={`flex flex-row justify-end ${sourcesExpanded ? 'mt-2' : ''}`}>
                              <SourcesPane slide={selectedSlide} onToggle={setSourcesExpanded} />
                            </div>}
                        </div>}
                      {!selectedSlide.slideDescription && (!selectedSlide.media || selectedSlide.media.length === 0) && <div className="flex flex-col items-center justify-center w-full h-full min-h-[200px] text-white/70">
                          <div className="text-center">
                            <p className="text-lg mb-4">No content available for this slide</p>
                            {selectedSlide.sources && selectedSlide.sources.length > 0 && <div className={`flex justify-center ${sourcesExpanded ? 'mt-4' : ''}`}>
                                <SourcesPane slide={selectedSlide} onToggle={setSourcesExpanded} />
                              </div>}
                          </div>
                        </div>}
                    </div>
                  </div>
                  <div data-testid='next-button' className='hidden sm:block'>
                    <button data-testid="NextButton" onClick={event => {
                    handleNavigation('next');
                    recordStat('NextSlideButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                  }} disabled={currentIndex === allSlidesWithTitleSlide.length - 1} className={`px-2 py-1.5 h-fit w-fit flex-none rounded-full bg-transparent outline-none ${currentIndex < allSlidesWithTitleSlide.length - 1 ? 'cursor-pointer hover:bg-slate-700/30' : ''} text-white`}>
                      <motion.div className='bg-transparent' initial={false} animate={{
                      color: nextButtonActive ? '#63b4ff' : 'white',
                      scale: currentIndex < allSlidesWithTitleSlide.length - 1 ? 1 : 1,
                      opacity: currentIndex < allSlidesWithTitleSlide.length - 1 ? 1 : 0.5
                    }} whileHover={{
                      scale: currentIndex < allSlidesWithTitleSlide.length - 1 ? 1.20 : 1
                    }} whileTap={{
                      scale: currentIndex < allSlidesWithTitleSlide.length - 1 ? 0.95 : 1
                    }} transition={{
                      duration: 0.2
                    }}>
                        <ArrowCircleRightIcon weight="duotone" className={`h-12 w-12 pt-[2px] ${currentIndex === allSlidesWithTitleSlide.length - 1 ? 'opacity-20' : ''}`} />
                      </motion.div>
                    </button>
                  </div>
                </div>
              </CarouselItem>
            </Carousel>
        </div>}
      </motion.div>
      <button className='hidden sm:flex flex-row items-center gap-2 absolute top-[10px] right-[10px] z-50 btn-h text-white/65 hover:text-white' onClick={() => handleOpenModal(selectedSlide)}>
        <PencilIcon size={24} weight="duotone" data-sentry-element="PencilIcon" data-sentry-source-file="tp-presentation.tsx" /> <span className="text-[14px]">Edit Event</span>
      </button>
    </div>
    </div>;
};
export default TPPresentation;